import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
import TemplateSettingsForm, { TemplateSettingsFormFields } from './components/Form'
// hooks
import { useModalData } from 'hooks/useShowModal'
import { useCurrentUser } from 'hooks/useCurrentUser'
// constants
import { Modals } from 'constants/modals'
// types
import { IRiskTemplateUpdateInput } from 'types/graphql'
// graphql
import { RISK_TEMPLATE_UPDATE_MUTATION } from 'graphql/risk/mutations'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// themes
import { CloseIcon } from 'components/UI/Icons'
// utils
import omit from 'lodash/omit'
import { handleError } from 'utils/errors/handlers'

const PAPER_PROPS = {
  style: {
    top: 0,
    height: '100%',
    overflow: 'hidden'
  }
}

const TemplateSettingsDrawer: React.FC = () => {
  const currentUser = useCurrentUser()

  const aiManageSettings = currentUser.permissionsFromSecurityGroups.aiManageSettings

  const [updateTemplateMutation] = useMutation(RISK_TEMPLATE_UPDATE_MUTATION)

  const { riskTemplate, refetchTemplate } = React.useContext(RiskTemplateAPIContext)

  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.RISK_TEMPLATE_SETTINGS_MODAL })

  const updateTemplate = async (input: IRiskTemplateUpdateInput) => {
    await updateTemplateMutation({
      variables: {
        id: riskTemplate.id,
        input: omit(input, ['createUrl', 'publicCreateUrl'])
      }
    })
  }

  const onSubmit = async (data: TemplateSettingsFormFields) => {
    try {
      await updateTemplate(data)

      refetchTemplate()

      hideModal()
    } catch (e) {
      handleError(e, 'TemplateSettingsDrawer.onSubmit')
    }
  }

  const createUrl = `${window.location.origin}/forms/create/${riskTemplate?.id}`
  const publicCreateUrl = `${window.location.origin}/public/form/create/${riskTemplate?.id}?domain=${currentUser.company.domainName}`

  const initialValues = riskTemplate
    ? {
        createUrl,
        publicCreateUrl,
        isGuestAccess: riskTemplate.isGuestAccess,
        isPublicSearchable: riskTemplate.isPublicSearchable,
        customerUniqueIdPrefix: riskTemplate.customerUniqueIdPrefix,
        generateCustomerUniqueId: riskTemplate.generateCustomerUniqueId,
        archiveOnDelete: riskTemplate.archiveOnDelete,
        ...(aiManageSettings && {
          aiSuggestedPrompts: riskTemplate.aiSuggestedPrompts,
          aiEnabled: riskTemplate.aiEnabled,
          aiContextDescription: riskTemplate.aiContextDescription,
          includeInAllAiContext: riskTemplate.includeInAllAiContext
        })
      }
    : {}

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Template Settings</span>
        <CloseIcon onClick={hideModal} />
      </Header>
      <TemplateSettingsForm
        onSubmit={onSubmit}
        createUrl={createUrl}
        hideModal={hideModal}
        initialValues={initialValues}
        publicCreateUrl={publicCreateUrl}
      />
    </DrawerModal>
  )
}

export default TemplateSettingsDrawer
